import { useState, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components/macro";
import {
  BoxHeader,
  BoxNavLeft,
  BoxTitle,
  BoxNavRight,
  BoxNavButton,
  BoxContent,
  BoxFooter,
  BoxNavLink,
} from "./Box";
import { useSkipioApi, touchSkipioApi } from "../api";
import SidePanel, { SidePanelTrigger } from "./SidePanel";
import { Close } from "./Icons";
import { GroupScheduledMessages } from "./ScheduledMessages";
import ContactsWell from "./ContactsWell";
import Stack from "./Stack";
import Input, { Label } from "./Input";
import { useFields } from "../hooks/use-fields";
import Button from "./Button";
import { useToast } from "../hooks/use-toast";
import useExitWarning from "../hooks/use-prevent-exit";
import usePlan from "../hooks/use-plan";
import usePaywall from "../hooks/use-paywall";

import { Switch, Route, Redirect } from "react-router";
import AutomationsEditor from "./AutomationsEditor";
import { useSkipioQuery } from "../hooks/use-query";

function GroupViewer({
  groupId,
  hide,
  contactIds,
}: {
  groupId?: string;
  hide?: () => void;
  contactIds?: string[];
}) {
  const groupQuery = useSkipioQuery({
    queryKey: ["groups", groupId],
    enabled: !!groupId,
  });
  const group = groupId ? groupQuery.data : null;

  const readOnly = Boolean(group && (group.systemCreated || group.isSmart));

  return (
    <>
      <BoxHeader stacked={Boolean(groupId && !readOnly)}>
        <BoxNavLeft>
          <BoxTitle>
            <span
              style={{
                maxWidth: "500px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                height: "2em",
                lineHeight: "2em",
              }}
            >
              {group ? group.name : "New Group"}
            </span>
          </BoxTitle>
        </BoxNavLeft>
        <BoxNavRight>
          {Boolean(hide) && (
            <BoxNavButton onClick={() => hide!()}>
              <Close />
            </BoxNavButton>
          )}
        </BoxNavRight>
      </BoxHeader>
      {Boolean(groupId && !readOnly) && (
        <BoxHeader>
          <BoxNavLeft>
            <BoxNavLink to="/details">Details</BoxNavLink>
            <BoxNavLink to="/rules">Rules</BoxNavLink>
          </BoxNavLeft>
        </BoxHeader>
      )}
      <Switch>
        <Route path="/details">
          <GroupEditor groupId={groupId} hide={hide} contactIds={contactIds} />
        </Route>
        <Route path="/rules">
          <BoxContent>
            <Suspense fallback={null}>
              <AutomationsEditor
                automationAction={{ contactGroupId: groupId }}
              />
            </Suspense>
          </BoxContent>
        </Route>
        <Redirect to="/details" />
      </Switch>
    </>
  );
}

export function GroupViewerSidePanel({
  trigger,
  groupId,
  contactIds,
}: {
  trigger: SidePanelTrigger;
  groupId?: string;
  contactIds?: string[];
}) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => (
        <GroupViewer
          hide={() => hide()}
          groupId={groupId}
          contactIds={contactIds}
        />
      )}
    />
  );
}

function GroupEditor({
  groupId,
  hide,
  contactIds = [],
}: {
  groupId?: string;
  contactIds?: string[];
  hide?: () => void;
}) {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const isCreate = !groupId;
  const groupQuery = useSkipioQuery({
    queryKey: ["groups", groupId],
    enabled: !!groupId,
  });
  const group = groupId ? groupQuery.data : { name: "", contactIds };

  const readOnly = Boolean(group.systemCreated);

  const [value, onChange] = useState(group);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { field, isChanged, reset } = useFields({ value, onChange });

  useExitWarning(isChanged);

  const showToast = useToast();
  const plan = usePlan();
  const showPaywall = usePaywall();

  async function save() {
    const caller = !groupId
      ? skipioApi.groups.post
      : skipioApi.groups[groupId].put;

    if (isCreate && !plan.canMakeGroup) {
      return showPaywall(
        "Looks like you're ready to add another group. Great! Let's upgrade your account."
      );
    }

    setErrors({});

    try {
      const {
        data: { data: newGroup },
      } = await caller({}, { data: { group: value } });

      const deletedIds = newGroup.contactIds.filter(
        (id: string) => !value.contactIds.includes(id)
      );

      const addedIds = value.contactIds.filter(
        (id: string) => !newGroup.contactIds.includes(id)
      );

      if (deletedIds.length) {
        await skipioApi.groups[newGroup.id].contacts.delete(
          {},
          { data: { contactId: [...deletedIds], skipDefer: true } }
        );
      }

      if (addedIds.length) {
        await skipioApi.groups[newGroup.id].contacts.post(
          {},
          { data: { contactId: [...addedIds], skipDefer: true } }
        );
      }

      await touchSkipioApi("groups", "contacts", "addressBook");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      await queryClient.invalidateQueries({ queryKey: ["addressBook"] });

      showToast(isCreate ? "Group Created" : "Group Updated", {
        success: true,
      });

      if (hide) hide();
    } catch (e) {
      try {
        setErrors(e.response.data);
      } catch (_) {
        throw e;
      }
    }
  }

  return (
    <>
      <FlexedBoxContent>
        <Stack space={6}>
          {!readOnly && (
            <Input {...field("name")} error={errors.name} label="Group Name" />
          )}
          <>
            <Label>Contacts</Label>
            <ContactsWell
              value={{
                contactIds: value.contactIds,
                groupIds: [],
                keywordIds: [],
              }}
              onChange={(patch) => {
                onChange((value: any) => {
                  const newValue =
                    typeof patch === "function"
                      ? { ...value, ...patch(value) }
                      : { ...value, ...patch };
                  return newValue;
                });
              }}
              canAdd={!readOnly}
              canRemove={!readOnly}
            />
          </>
          {!isCreate && (
            <>
              <Label>Scheduled Messages</Label>
              <GroupScheduledMessages groupId={groupId} />
            </>
          )}
        </Stack>
      </FlexedBoxContent>
      {!readOnly && (
        <BoxFooter>
          {isChanged && (
            <BoxNavLeft>
              <Button onClick={reset} variant="link">
                Cancel
              </Button>
            </BoxNavLeft>
          )}
          <BoxNavRight>
            <Button onClick={() => save()} disabled={!isChanged}>
              Save
            </Button>
          </BoxNavRight>
        </BoxFooter>
      )}
    </>
  );
}

export default GroupViewer;

const FlexedBoxContent = styled(BoxContent)`
  display: flex;
  flex-direction: column;
`;
